import { cloneDeep, get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import findResult, {
  addUniqueItemByCode,
  assignIfExists,
  assignValue,
  findMatch,
  hasKey,
  isEmpty,
  isPlainObject,
  removeEmpty,
  removeItemByCode,
  selectableResultWithDates,
} from '@/utils/resultsHelper';
import { pruneFieldErrors, responseError } from '@/utils/errors';
import { states, saveStates } from '@/constants/lifecycle';
import pagingWithFilteredResults, {
  openEntityDialog,
  presentationOptions,
  sortingFilters,
  visibilityFilters,
} from '@/constants/states';
import fieldNames from '@/constants/fields';
import { newDatasetUrl } from '@/constants/urls';
import { isCommonsModeEnabled, isPmnTransferEnabled } from '@/auth/featureToggles';
import { formSections } from '@/forms/state/state';
import findDataset from './shared';
import { datasetCollection } from '../collections/state/state';
import {
  datasetLists,
  datasetSourceEntities,
  datasetStates,
  datasetSteps,
  datasetStepsWithCatalogue,
} from '../constants';
import { datasetSettingsFields } from '../settings/state/state';
import datasetStatusInitialState from '../status/state/state';
import { datasetPerms } from '../sharing/state/state';
import attachmentsInitialState, {
  activeAttachmentInitialState,
  activeFolderInitialState,
  createFolderInitialState,
  downloadableAttachmentConfirmationState,
} from '../attachments/state/state';
import customActionsInitialState from '../links/state/state';
import { cataloguePanels } from '../catalogue/templates/state/state';
import crushCatalogueProps, { activeCatalogueFieldArray, assignCatalogueProps } from '../forms/state/state';
import {
  activeDictionaries,
  activeDictionary,
  activeDictionaryEdit,
  splitUrlsInDictionaries,
} from '../dictionaries/state/state';
import { removeUUIDsFromDictionaries } from '../dictionaries/helper';
import { datasetPermissions, nonDatasetSharingPermissions } from '../sharing/constants';

const allDatasets = (
  privateFlag,
  internalFlag,
  publicFlag,
  dataModelCode,
  orderBy = [{ column: 'name', direction: 'asc' }],
  singleSorting = true,
  display = presentationOptions.TABLE,
) => (
  {
    all: {
      ...pagingWithFilteredResults(),
      ...datasetStates,
      ...visibilityFilters(privateFlag, internalFlag, publicFlag),
      ...sortingFilters(orderBy, singleSorting),
      dataModelCode,
      display,
      multiSelect: true,
    },
  }
);

export const activeDataset = () => ({
  ...openEntityDialog,
  settings: datasetSettingsFields(),
  catalogue: {},
  template: formSections(),
  steps: { code: null, sourceUrl: null },
  ...saveStates,
});

const initialState = {
  activeAttachment: activeAttachmentInitialState(),
  activeFolder: activeFolderInitialState(),
  createFolder: createFolderInitialState(),
  activeDataset: activeDataset(),
  activeDictionary: activeDictionary(),
  activeDictionaryEdit: activeDictionaryEdit(),
  allowPmnTransfer: isPmnTransferEnabled(),
  catalogueFieldItems: activeCatalogueFieldArray(),
  counts: {
    dataSources: 10,
    datasetsAccessed: 14,
    datasetsSearchable: 18,
    datasetsUploaded: 12,
  },
  customActions: { ...customActionsInitialState() },
  datasets: {
    ...allDatasets(true, true, isCommonsModeEnabled()),
    admin: {
      results: [],
    },
    downloadableAttachmentConfirmation: downloadableAttachmentConfirmationState(),
    attachmentsDeletion: {
      results: [],
    },
    deletion: {
      results: [],
    },
    open: {
      results: [],
    },
    ribbon: {
      params: ['favourites'],
      ...pagingWithFilteredResults(5),
      ...sortingFilters(),
      ...datasetStates,
    },
    dataManagement: {
      results: [],
    },
  },
  deleteDatasetAttachmentOpen: false,
  deleteDatasetOpen: false,
  lookups: { ...activeDictionaries() },
  newDatasetCode: uuidv4(),
  transferDatasetOpen: false,
  wizardOff: false,
};

export const mapResult = (result) => selectableResultWithDates(result);

export const datasetStepNames = (steps) => Object.values(steps).map(({ name }) => name);

export const datasetStepName = (step) => datasetStepNames(datasetSteps)[step];

export const datasetStepIndexOf = (name) => datasetStepNames(datasetSteps).indexOf(name);

export const datasetStepIndexesOf = (names) => names.map(datasetStepIndexOf);

export const datasetStepsIncludes = (steps, step) => (datasetStepNames(steps).includes(datasetStepName(step)));

const allowedStepNames = (permissions, allowAttachments) => {
  const names = datasetStepNames(datasetStepsWithCatalogue);

  if (!allowAttachments || !permissions[datasetPermissions.ATTACH]) {
    names.splice(names.indexOf(datasetSteps.ATTACHMENTS.name), 1);
  }

  if (!Object.values(nonDatasetSharingPermissions).some((key) => permissions[key])) {
    names.splice(names.indexOf(datasetSteps.SETTINGS.name), 1);
  }

  return names;
};

export const allowedDatasetStepIndexes = ({ permissions }, { sections }, allowAttachments) => {
  const indexes = datasetStepIndexesOf(allowedStepNames(permissions, allowAttachments));
  const sectionIndexes = removeEmpty(Object.keys(sections).map((name, index) => (
    sections[name].fields.length === 0 ? null : index + 2
  )));

  if (sectionIndexes.length === 0) {
    sectionIndexes.push(1);
  }
  indexes.splice(1, 0, ...sectionIndexes);

  return indexes;
};

export const nextDatasetStepIndex = ({ permissions, steps }, { template, allowAttachments }) => {
  const indexes = allowedDatasetStepIndexes({ permissions }, template, allowAttachments);
  return indexes[indexes.indexOf(steps.activeStepIndex) + 1];
};

export const previousDatasetStepIndex = ({ permissions, steps }, { template, allowAttachments }) => {
  const indexes = allowedDatasetStepIndexes({ permissions }, template, allowAttachments);
  return indexes[indexes.indexOf(steps.activeStepIndex) - 1];
};

export const datasetIsDirty = (dataset) => (
  dataset && isPlainObject(dataset.isDirty) ? Object.values(dataset.isDirty).includes(true) : false
);

export const markDatasetAsDirty = (dataset, { name }, what = 'catalogue') => {
  const index = datasetStepIndexOf(name);
  assignValue(dataset.isDirty, what, true);
  if (!dataset.steps.wizardOn && dataset.steps.activeStepIndex > index) {
    assignValue(dataset.steps, 'activeStepIndex', index);
  }
};

export const datasetDirtyFlags = () => ({
  attachments: false,
  catalogue: false,
  datafiles: false,
  datasource: false,
  dictionaries: false,
  dictionaryfile: false,
  template: false,
});

export const newDatasetProperties = (item, template) => {
  const copy = cloneDeep(item);
  return ({
    ...copy,
    dictionaries: removeUUIDsFromDictionaries(copy.dictionaries),
    open: true,
    steps: { code: item.steps.code, sourceUrl: item.steps.sourceUrl },
    template: cloneDeep(template),
  });
};

export const assignEditPropsNoSplit = (dataset) => {
  const firstRun = !dataset.copy || !dataset.isEditing;
  if (firstRun) {
    const { previousTitle } = dataset.steps;
    assignValue(dataset.copy, 'catalogue', dataset.isNew ? {} : cloneDeep(dataset.catalogue));
    assignValue(dataset.copy, 'dictionaries', dataset.isNew ? [] : cloneDeep(dataset.dictionaries));
    if (previousTitle) {
      assignValue(dataset.copy.catalogue, 'title', previousTitle);
      assignValue(dataset.steps, 'previousTitle', null);
    }
  }

  if (!dataset.saveErrors) {
    assignValue(dataset, 'saveErrors', []);
  }

  if (!dataset.saveStatus) {
    assignValue(dataset, 'saveStatus', states.NONE);
  }

  assignValue(dataset, 'isEditing', true);
  return firstRun;
};

export const assignEditProps = (dataset, template, crush = false) => {
  const requiresSplit = template.code && assignEditPropsNoSplit(dataset);
  if (requiresSplit) {
    splitUrlsInDictionaries(dataset);
    return crush ? crushCatalogueProps(dataset, template) : assignCatalogueProps(dataset, template);
  }

  return false;
};

export const datasetSourceAndStepAttributes = (
  code = null,
  datasetSourceCode = null,
  datasetSourceType = datasetSourceEntities.DATASET,
  wizardOn = false,
) => ({
  activeStepIndex: 0,
  code: datasetSourceCode,
  previousTitle: null,
  sourceType: datasetSourceType,
  sourceUrl: datasetSourceCode ? newDatasetUrl(datasetSourceType, decodeURIComponent(datasetSourceCode), code) : null,
  templateCode: null,
  templateName: null,
  wizardOn,
});

export const openDataset = ({ code, datasetSourceCode, datasetSourceType }, wizardOn = false, can = false) => ({
  asViewer: { permissions: datasetPerms(), on: false },
  attachments: attachmentsInitialState(),
  copy: {},
  catalogue: { title: '' },
  code,
  customActions: customActionsInitialState(),
  dictionaries: [],
  permissions: datasetPerms(can),
  readErrors: [],
  readStatus: states.PENDING,
  ...saveStates,
  settings: datasetSettingsFields(),
  status: datasetStatusInitialState(),
  steps: datasetSourceAndStepAttributes(code, datasetSourceCode, datasetSourceType, wizardOn),
  subscriptions: [],
  ...cataloguePanels(),
});

export const newDataset = (action, wizardOn) => ({
  ...openDataset(action, wizardOn, true),
  activeDictionaryName: '',
  activeDictionaryUUID: null,
  id: null,
  isDirty: datasetDirtyFlags(),
  isEditing: false,
  isNew: true,
});

export const pruneDatasetErrors = (errors, { properties }) => {
  let filteredErrors = errors;

  if (hasKey(properties, 'name')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fieldNames.NAME);
  }
  if (hasKey(properties, 'code')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fieldNames.CODE);
  }
  if (hasKey(properties, 'description')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fieldNames.DESCRIPTION);
  }

  return filteredErrors;
};

export const datasetStateToLoad = ({ datasets, wizardOff }) => {
  const items = get(datasets, 'datasets', initialState.datasets);
  const allFilters = items.all || initialState.datasets.all;
  return {
    ...initialState,
    wizardOff: isEmpty(wizardOff) ? initialState.wizardOff : wizardOff,
    datasets: {
      ...initialState.datasets,
      ...allDatasets(
        allFilters.privateFlag,
        allFilters.internalFlag,
        allFilters.publicFlag,
        allFilters.dataModelCode,
        allFilters.orderBy,
        allFilters.singleSorting,
        allFilters.display,
      ),
    },
  };
};

export const datasetStateToSave = ({ datasets }) => (
  {
    wizardOff: datasets.wizardOff,
    datasets: {
      datasets: {
        all: {
          internalFlag: datasets.datasets.all.internalFlag,
          privateFlag: datasets.datasets.all.privateFlag,
          publicFlag: datasets.datasets.all.publicFlag,
          dataModelCode: datasets.datasets.all.dataModelCode,
          orderBy: datasets.datasets.all.orderBy,
          singleSorting: datasets.datasets.all.singleSorting,
          display: datasets.datasets.all.display,
        },
      },
    },
  }
);

export const resetDataset = (dataset, wizardOff = false) => {
  const { isEditing, steps, settings } = dataset;
  const properties = { steps: { templateCode: null, templateName: null } };
  if (wizardOff) { properties.steps.wizardOn = false; }
  if (!isEmpty(steps?.templateCode) && (steps?.templateCode !== settings.catalogue_definition_code)) {
    properties.settings = { catalogue_definition_code: steps.templateCode };
  }

  if (isEditing) {
    assignValue(dataset, 'catalogue', cloneDeep(dataset.copy.catalogue));
    assignValue(dataset, 'dictionaries', cloneDeep(dataset.copy.dictionaries));
    assignValue(dataset, 'copy', {});
    properties.saveStatus = states.NONE;
    properties.saveErrors = [];
    properties.isDirty = datasetDirtyFlags();
    properties.isEditing = false;
  }

  assignIfExists(dataset, properties);
  return isEditing;
};

export const switchOffDatasetPreview = (dataset) => {
  const changing = dataset.asViewer?.on;
  if (changing) {
    assignValue(dataset.asViewer, 'on', false);
    assignValue(dataset, 'permissions', { ...dataset.asViewer.permissions });
    assignValue(dataset.asViewer, 'permissions', datasetPerms());
  }

  return changing;
};

export const switchOnDatasetPreview = (dataset) => {
  const changing = !dataset.asViewer?.on;
  if (changing) {
    assignValue(dataset.asViewer, 'on', true);
    assignValue(dataset.asViewer, 'permissions', { ...dataset.permissions });
    assignValue(dataset, 'permissions', datasetPerms());
  }

  return changing;
};

export const isDatasetWizardOn = ({
  isNew,
  asViewer,
  permissions,
  steps,
}, role) => (
  steps.wizardOn && !asViewer.on && (isNew ? role.permissions.datasets.create
    : role.permissions.datasets.update && permissions[datasetPermissions.UPDATE])
);

export const closeDatasetState = (draft, { category, code }) => {
  const item = findDataset(draft, { category, code });
  if (item) {
    draft.datasets[category].results = removeItemByCode(draft.datasets[category].results, item);
  }
};

export const addDatasetListCollectionsState = (draft, { code, items }) => {
  datasetLists.forEach((category) => {
    const item = findResult(draft.datasets, category, code);
    if (item?.collections) {
      items.forEach((collection) => {
        const datasetCollectionToAdd = datasetCollection();
        assignIfExists(datasetCollectionToAdd, collection);
        item.collections = addUniqueItemByCode(item.collections, datasetCollectionToAdd);
      });
    }
  });
};

export const removeDatasetListCollectionsState = (draft, { code, items }) => {
  datasetLists.forEach((category) => {
    const item = findResult(draft.datasets, category, code);
    if (item?.collections) {
      items.forEach((collection) => {
        item.collections = removeItemByCode(item.collections, collection);
      });
    }
  });
};

export const showDatasetErrorState = (draft, action) => {
  const item = findDataset(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
};

export const updateDatasetListCollectionsState = (draft, collection) => {
  datasetLists.forEach((category) => {
    (draft.datasets[category]?.results || []).forEach((dataset) => {
      const match = findMatch(dataset.collections || [], collection.code);
      if (match) {
        assignIfExists(match, collection);
      }
    });
  });
};

export default initialState;
